import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { ArrowLeft, X } from "lucide-react";
import packingService from "../Services/packingService";
import orderService from "../Services/orderService";
import packedProductsService from "../Services/packedProductsService";
import toast from "react-hot-toast";
import warehouseService from "../Services/warehouseService";
import scannedItemsService from "../Services/scannedItemsService";
import * as XLSX from "xlsx";

export default function PackingView() {
  const { id } = useParams();
  const [packing, setPacking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addWithoutBarcode, setAddWithoutBarcode] = useState(false);
  const [warehouseProducts, setWarehouseProducts] = useState();
  const [error, setError] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();

  const fileInputRef = useRef(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState([]);

  const warehouseQtyMap = (warehouseProducts || []).reduce((acc, p) => {
    const key = String(p.part_number || "").trim();
    if (!key) return acc;

    const qty = Number(p.quantity || 0);

    acc[key] = (acc[key] || 0) + (Number.isNaN(qty) ? 0 : qty);

    return acc;
  }, {});

  useEffect(() => {
    const fetchPacking = async () => {
      try {
        const res = await packingService.getSingle(id);
        if (res.responseCode === 200 && res.packing) {
          const productsWithEdit = res.packing.products.map((p) => ({
            ...p,
            editQuantity: p.quantityPlaced || "",
          }));
          setPacking({ ...res.packing, products: productsWithEdit });
        } else {
          setPacking(null);
        }
      } catch (error) {
        console.error("Error fetching packing:", error);
        setPacking(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPacking();
  }, [id]);

  useEffect(() => {
    const fetchWarehouseProducts = async () => {
      try {
        const res = await scannedItemsService.fetchAll();
        console.log("FULL API RESPONSE:", res);
        console.log("SCANNED ITEMS:", res?.scanneditems);
        console.log("FIRST ITEM:", res?.scanneditems?.[0]);

        setWarehouseProducts(res?.scanneditems || []);
      } catch (e) {
        console.error(e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchWarehouseProducts();
  }, []);

  const savePackingToDB = async (products) => {
    const payload = {
      items: (products || []).map((p) => ({
        detail_id: p.id,
        quantityPlaced: Number(p.quantityPlaced) || 0,
      })),
    };

    return packingService.place(id, payload);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xls|xlsx)$/i)) {
      toast.error("Please upload an Excel file (.xls or .xlsx)");
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (!jsonData || jsonData.length === 0) {
          toast.error("Excel file is empty or invalid");
          return;
        }

        const processedData = jsonData
          .map((row) => {
            const excelPart = String(
              row.partnumber ??
                row["partnumber"] ??
                row.partnumber_1 ??
                row["partnumber_1"] ??
                row.PartNumber ??
                row["PartNumber"] ??
                "",
            ).trim();

            if (!excelPart) return null;

            const qtyValue =
              row.QuantityPlaced ??
              row.quantityPlaced ??
              row["QuantityPlaced"] ??
              row["quantityPlaced"] ??
              row.QuantityPlacer ??
              row["QuantityPlacer"] ??
              row.Quantity ??
              row["Quantity"] ??
              0;
            const newQty = Number(qtyValue);

            const matchingProduct = packing?.products?.find(
              (product) =>
                String(product.partnumber || "").trim() === excelPart,
            );

            if (!matchingProduct) return null;

            let safeQty = Number.isNaN(newQty) || newQty < 0 ? 0 : newQty;

            const stockQty = warehouseQtyMap[excelPart];
            if (typeof stockQty === "number" && safeQty > stockQty) {
              safeQty = stockQty;
              toast.error(
                `Only ${stockQty} available in warehouse for ${excelPart}`,
              );
            }

            const excelMake = String(row.make ?? row.Make ?? "").trim();

            const boxValue = String(
              row.BoxNumber ?? row.boxNumber ?? row["BoxNumber"] ?? "",
            ).trim();
            const boxLocations = boxValue ? [boxValue] : [];

            const currentPacked =
              matchingProduct.quantityPlaced !== null &&
              matchingProduct.quantityPlaced !== undefined &&
              String(matchingProduct.quantityPlaced).trim() !== ""
                ? Number(matchingProduct.quantityPlaced)
                : matchingProduct.editQuantity !== null &&
                    matchingProduct.editQuantity !== undefined &&
                    String(matchingProduct.editQuantity).trim() !== ""
                  ? Number(matchingProduct.editQuantity)
                  : 0;

            return {
              partnumber: excelPart,
              maker: excelMake || matchingProduct.maker,
              description:
                String(row.description ?? row.Description ?? "").trim() ||
                matchingProduct.description,
              currentQuantity: matchingProduct.quantity,
              currentQuantityPlaced: currentPacked,
              newQuantityPlaced: safeQty,

              locations: boxLocations.length
                ? boxLocations
                : matchingProduct.locations || [],
              matched: true,
            };
          })
          .filter(Boolean);

        if (processedData.length === 0) {
          toast.error("No matching products found in the Excel file");
          return;
        }

        setPreviewData(processedData);
        setShowPreviewModal(true);
      } catch (err) {
        console.error(err);
        toast.error("Failed to read Excel file");
      }
    };

    reader.readAsArrayBuffer(file);
    e.target.value = null;
  };

  const confirmUpload = async () => {
    if (!packing) return;

    const updatedProducts = packing.products.map((product) => {
      const match = previewData.find(
        (item) => item.partnumber === String(product.partnumber || "").trim(),
      );

      if (!match) return product;

      return {
        ...product,
        quantityPlaced: match.newQuantityPlaced,
        editQuantity: match.newQuantityPlaced,
        locations: match.locations || product.locations || [],
      };
    });

    setPacking((prev) =>
      prev ? { ...prev, products: updatedProducts } : prev,
    );

    try {
      await savePackingToDB(updatedProducts);
      toast.success("Quantities updated & saved successfully");
      setShowPreviewModal(false);
      setPreviewData([]);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save quantities to database");
    }
  };

  const cancelUpload = () => {
    setShowPreviewModal(false);
    setPreviewData([]);
    toast.error("Upload cancelled");
  };

  const allCompleted =
    Array.isArray(packing?.products) &&
    packing.products.every((item) => {
      const qty = addWithoutBarcode ? item.editQuantity : item.quantityPlaced;
      return qty !== null && qty !== "" && qty !== undefined && Number(qty) > 0;
    });

  const status = packing?.status?.toLowerCase().trim();
  const isLockedByStatus =
    status === "readytodeliver" ||
    status === "dispatched" ||
    status === "deliveredorders" ||
    status === "completed";

  const canCompletePacking = status === "underprocess";

  const completePacking = async (productsArg, forceWithoutBarcode = false) => {
    try {
      if (isLockedByStatus || !canCompletePacking) {
        toast.error("Packing is locked or not in Under Process status.");
        return;
      }

      if (!packing || !packing.invoice) {
        toast.error("Packing or invoice not found!");
        return;
      }

      const productsToUse = Array.isArray(productsArg)
        ? productsArg
        : packing.products;
      const useWithoutBarcode = forceWithoutBarcode || addWithoutBarcode;

      const orderRes = await orderService.fetchByInvoice(packing.invoice);

      if (orderRes.responseCode !== 200 || !orderRes.order) {
        toast.error("Order not found!");
        return;
      }

      const order = orderRes.order;

      let finalAmount = 0;
      let finalVatAmount = 0;

      const payloadProducts = productsToUse
        .map((p) => {
          const detail = order.details.find(
            (d) => d.vendor_code === p.partnumber,
          );
          if (!detail) return null;

          const unitPrice = parseFloat(detail.quote_price);
          const qtyPlaced = Number(
            useWithoutBarcode ? p.editQuantity : p.quantityPlaced,
          );
          const vatPercent = Number(detail.vat_percentage);

          const amount = unitPrice * qtyPlaced;
          const vat = amount * (vatPercent / 100);

          finalAmount += amount;
          finalVatAmount += vat + amount;

          return {
            id: detail.id,
            order: order.id,
            product: detail.product,
            vendor_code: p.partnumber,
            brand: p.maker,
            title: p.description,
            quantity: qtyPlaced,
            price: unitPrice,
            invoice_quantity: qtyPlaced,
            invoice_price: amount.toFixed(2),
            vat_percentage: vatPercent,
          };
        })
        .filter(Boolean);

      const payload = {
        order: {
          id: order.id,
          amount: parseFloat(finalAmount.toFixed(2)),
          vat_amount: parseFloat(finalVatAmount.toFixed(2)),
          status: "readytodeliver",
        },
        products: payloadProducts,
      };

      await orderService.updateOrder(payload);

      if (useWithoutBarcode) {
        for (const p of productsToUse) {
          const qtyPlaced = Number(p.editQuantity);
          if (qtyPlaced > 0) {
            await packedProductsService.create({
              order: order.id,
              customer: order.customer,
              packing_id: packing.id,
              name: p.description,
              part_number: p.partnumber,
              quantity: qtyPlaced,
              price:
                payloadProducts.find((x) => x.vendor_code === p.partnumber)
                  ?.price || 0,
              made_by: p.maker,
              barcode: p.barcode || "",
              hs_code: p.hs_code || "",
              locations: p.locations || [],
            });
          }
        }
      }

      setPacking((prev) => ({ ...prev, status: "Completed" }));

      toast.success("Packing completed & Order updated!");

      setTimeout(() => {
        navigate(`/order/view?invoice=${packing.invoice}`);
      }, 800);
    } catch (error) {
      console.error("Error completing packing:", error);
      toast.error(error.message || "Something went wrong!");
    }
  };

  const applyExcelAndCompletePacking = async () => {
    if (!packing) return;

    if (isLockedByStatus || !canCompletePacking) {
      toast.error("Packing is locked or not in Under Process status.");
      return;
    }

    const updatedProducts = packing.products.map((product) => {
      const match = previewData.find(
        (item) => item.partnumber === String(product.partnumber || "").trim(),
      );

      if (!match) return product;

      return {
        ...product,
        quantityPlaced: match.newQuantityPlaced,
        editQuantity: match.newQuantityPlaced,
        locations: match.locations || product.locations || [],
      };
    });

    setPacking((prev) =>
      prev ? { ...prev, products: updatedProducts } : prev,
    );

    setShowPreviewModal(false);
    setPreviewData([]);

    setAddWithoutBarcode(true);

    await completePacking(updatedProducts, true);
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-gray-600 text-lg">Loading...</p>
      </div>
    );

  if (!packing)
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <p className="text-gray-500 text-lg">No packing found</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md"
        >
          Go Back
        </button>
      </div>
    );

  return (
    <div className="mx-10 p-8 rounded-3xl border border-gray-200 shadow-lg bg-white min-h-[calc(100vh-48px)]">
      <div>
        <h1 className="text-3xl font-bold text-secondary">Packing Details</h1>
      </div>
      <div className="bg-white border shadow-md w-[60%] p-10 mt-10 rounded-3xl">
        <div className="flex flex-row gap-3 items-center">
          <div className="text-cyan-700 text-xl font-semibold flex flex-row items-center gap-2">
            <h1>{packing.company_name}</h1>
          </div>
        </div>
        <div className="grid grid-cols-2">
          <div>
            <div className="text-sm font-medium text-gray-400 mt-4">
              <p>
                Packing ID:{" "}
                <span className="font-semibold text-gray-700">
                  {packing.id}
                </span>
              </p>
            </div>
            <div className="text-sm font-medium text-gray-400 mt-2">
              <p>
                Created At:{" "}
                <span className="font-semibold text-gray-700">
                  {moment(packing.created_at).format("lll")}
                </span>
              </p>
            </div>
            <div className="text-sm font-medium text-gray-400 mt-2">
              <p>
                Packing Date:{" "}
                <span className="font-semibold text-gray-700">
                  {moment(packing.date).format("ll")}
                </span>
              </p>
            </div>
            <div className="text-sm font-medium text-gray-400 mt-2">
              <p>
                Sale:{" "}
                <span className="font-semibold text-gray-700">
                  {packing.order || "N/A"}
                </span>
              </p>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-400 mt-2">
              <p>
                Description:{" "}
                <span className="font-semibold text-gray-700">
                  {packing.description || "N/A"}
                </span>
              </p>
            </div>
            <div className="text-sm font-medium text-gray-400 mt-2">
              <p>
                Status:{" "}
                <span
                  className={`inline-block px-3 py-1 rounded-md text-sm font-semibold tracking-normal ${
                    packing.status === "Completed"
                      ? "bg-green-100 text-green-600"
                      : packing.status === "Pending"
                        ? "bg-red-100 text-red-600"
                        : "bg-yellow-600/10 text-amber-600"
                  }`}
                >
                  {packing.status}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-row justify-between mt-10">
        <div className="font-bold text-2xl text-secondary">Packing Items</div>
        <button
          onClick={() => setShowDetails((prev) => !prev)}
          className="flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:shadow transition-all duration-300"
        >
          <svg
            className={`w-4 h-4 transition-transform duration-300 ${showDetails ? "rotate-180" : ""}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
          </svg>
          {showDetails ? "Hide Details" : `Show Details`}
        </button>
      </div>

      <div
        className={`transition-all duration-500 overflow-hidden ${showDetails ? "opacity-100 mt-4" : "max-h-0 opacity-0"}`}
      >
        <div className="table-div">
          <table className="w-full">
            <thead>
              <tr className="border-y-2 text-left w-full text-black text-[10px]">
                <th className="p-3 text-left">ID</th>
                <th className="p-3 text-left">Maker</th>
                <th className="p-3 text-left">Part No.</th>
                <th className="p-3 text-left">Part Name</th>
                <th className="p-3 text-left">Quantity</th>
                <th className="p-3 text-left">Quantity Packed</th>
                {addWithoutBarcode && (
                  <th className="p-3 text-left">Edit Placed Quantity</th>
                )}
              </tr>
            </thead>
            <tbody>
              {Array.isArray(packing.products) &&
              packing.products.length > 0 ? (
                packing.products.map((item, index) => (
                  <tr
                    key={index}
                    className="border-b hover:bg-gray-50 transition-colors"
                  >
                    <td className="p-3 font-semibold text-blue-500">
                      {index + 1}.
                    </td>
                    <td className="p-3 font-medium text-gray-800">
                      {item.maker}
                    </td>
                    <td className="p-3">{item.partnumber}</td>
                    <td className="p-3">{item.description || "-"}</td>
                    <td className="p-3">{item.quantity}</td>
                    <td className="p-3">{item.quantityPlaced}</td>

                    {addWithoutBarcode && (
                      <td className="p-3">
                        <input
                          type="number"
                          className="border px-2 py-1 w-20 rounded"
                          value={item.editQuantity || 0}
                          onChange={(e) => {
                            let newVal = Number(e.target.value);

                            const partNo = String(item.partnumber || "").trim();
                            const stockQty = warehouseQtyMap[partNo] ?? 0;

                            if (Number.isNaN(newVal) || newVal < 0) newVal = 0;

                            if (newVal > stockQty) {
                              newVal = stockQty;
                              toast.error(
                                `Only ${stockQty} available in warehouse for ${partNo}`,
                              );
                            }

                            setPacking((prev) => ({
                              ...prev,
                              products: prev.products.map((p, idx) =>
                                idx === index
                                  ? { ...p, editQuantity: newVal }
                                  : p,
                              ),
                            }));
                          }}
                          disabled={isLockedByStatus}
                        />
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center p-6 text-gray-500">
                    No items found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-4">
        <input
          type="file"
          accept=".xls,.xlsx"
          ref={fileInputRef}
          onChange={handleFileUpload}
          className="hidden"
        />

        <button
          onClick={() => {
            setAddWithoutBarcode(true);
            fileInputRef.current?.click();
          }}
          disabled={isLockedByStatus}
          className={`px-4 py-2 rounded-lg shadow ${
            isLockedByStatus
              ? "hidden"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          Upload File
        </button>

        {!addWithoutBarcode && (
          <button
            onClick={() => setAddWithoutBarcode(true)}
            disabled={isLockedByStatus}
            className={`button-primary px-4 py-2 rounded-lg shadow ${
              isLockedByStatus ? "hidden" : ""
            }`}
          >
            Add Products Without Barcode
          </button>
        )}

        {/* ✅ KEEP BUTTON VISIBLE ALWAYS, ONLY DISABLE LIKE OTHER BUTTONS */}
        <button
          onClick={completePacking}
          disabled={isLockedByStatus || !canCompletePacking}
          className={`px-6 py-2 rounded-lg font-semibold shadow ${
            isLockedByStatus || !canCompletePacking
              ? "hidden"
              : "bg-green-600 hover:bg-green-700 text-white"
          }`}
        >
          {addWithoutBarcode
            ? "Complete Packing Without Barcode"
            : "Complete Packing"}
        </button>
      </div>

      {/* ✅ Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  Preview Excel Data
                </h2>
                <p className="text-gray-500 text-xs mt-1">
                  Review before applying changes ({previewData.length} items
                  found)
                </p>
              </div>
              <button
                onClick={cancelUpload}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-6">
              <table className="min-w-full border border-gray-200 text-sm">
                <thead className="bg-gray-100 text-gray-700 uppercase text-xs sticky top-0">
                  <tr>
                    <th className="p-3 border">Part No.</th>
                    <th className="p-3 border">Maker</th>
                    <th className="p-3 border">Description</th>
                    <th className="p-3 border">Current Qty</th>
                    <th className="p-3 border">Current Packed</th>
                    <th className="p-3 border bg-green-50">New Packed Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((item, index) => (
                    <tr key={index} className="border-t hover:bg-gray-50">
                      <td className="p-3 border font-mono">
                        {item.partnumber}
                      </td>
                      <td className="p-3 border">{item.maker}</td>
                      <td className="p-3 border">{item.description}</td>
                      <td className="p-3 border text-center">
                        {item.currentQuantity}
                      </td>
                      <td className="p-3 border text-center">
                        {item.currentQuantityPlaced}
                      </td>
                      <td className="p-3 border text-center bg-green-50 font-semibold">
                        {item.newQuantityPlaced}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100 font-bold border-t-2">
                    <td colSpan="5" className="p-3 border text-right">
                      Total:
                    </td>
                    <td className="p-3 border text-center bg-green-100 text-green-700">
                      {previewData.reduce(
                        (sum, item) =>
                          sum + (Number(item.newQuantityPlaced) || 0),
                        0,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={cancelUpload}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-700 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={applyExcelAndCompletePacking}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold shadow"
              >
                Continue & Apply Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
